/**
 * 消費一覧・消費詳細の表示の言葉と値の形（プロダクト仕様書「表示の制御」）。
 * USDとクレジットは小数2桁、S3容量は小数3桁、割合と構成比は小数1桁で四捨五入し、USD・クレジット・円・時点値は3桁ごとにカンマで区切る。
 * 値がないときは「-」。
 */

import type { CategoryColumns, ItemValues } from '@/lib/api/credit-consumption';
import type { Failure } from '@/lib/api/credit-contracts';
import { formatDecimal, groupDigits } from '@/lib/credit-consumption/decimal';

/** 消費項目（列挙型定義書の「日本語」） */
export const itemLabels: Record<string, string> = {
  StoreSku: '店舗×SKU',
  S3Capacity: 'S3容量',
  UserCount: 'ユーザー数',
  DataConnect: 'データコネクト',
  StoreAgent: 'ストアエージェント',
  Athena: 'Athena',
  Bedrock: 'Bedrock',
  Fargate: 'Fargate',
  Glue: 'Glue',
  SageMaker: 'SageMaker',
};

/** 表示区分（列挙型定義書の「日本語」） */
export const categoryLabels: Record<string, string> = {
  DataVolume: 'データ量',
  UserCount: 'ユーザー数',
  DataConnect: 'データコネクト',
  StoreAgent: 'ストアエージェント',
  Compute: '計算量',
  FkToken: 'FKトークン',
};

export const NONE = '-';

/** USDとクレジット */
export const credit = (v: string | null | undefined) => (v ? formatDecimal(v, 2) : NONE);
export const percent = (v: string | null | undefined) => (v ? formatDecimal(v, 1) : NONE);
export const terabytes = (v: string | null | undefined) => (v ? formatDecimal(v, 3) : NONE);
/** 円・時点値（整数） */
export const integer = (v: string | null | undefined) => (v ? groupDigits(v) : NONE);

const reflectedFormat = new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** 反映時点（日本時間の `YYYY/MM/DD HH:mm`）。なければ「-」 */
export function reflectedText(iso: string | null): string {
  if (!iso) return NONE;
  const p = Object.fromEntries(
    reflectedFormat.formatToParts(new Date(iso)).map((x) => [x.type, x.value]),
  );
  return `${p.year}/${p.month}/${p.day} ${p.hour}:${p.minute}`;
}

export const CREDIT_UNIT = '（クレジット）';
/** USDとクレジットを組にした列の、一番下の段の見出し */
export const USD_LABEL = 'USD';
export const CREDIT_LABEL = 'クレジット';

/** 消費の内訳の値を持つ行（消費・USD） */
export interface BreakdownValues {
  items: Record<string, string>;
  category_totals: Record<string, string>;
  usd: ItemValues;
}

/** 消費の内訳の1列：消費項目か、表示区分の合計の、クレジットかUSD */
export interface BreakdownColumn {
  key: string;
  /** 上の段の見出し（表示区分の名前）。表示区分と消費項目の名前が同じときは null */
  group: string | null;
  label: string;
  /** USDとクレジットを組にした列の、label の下の段の見出し。組にしない列は null */
  sub: string | null;
  /** 組にしない列の単位 */
  unit?: string;
  value: (v: BreakdownValues) => string | null;
}

/**
 * 表示区分ごとにまとめた消費の内訳の列。表示区分と消費項目の名前が同じときは見出しを1段にし、
 * 消費項目が2つ以上属する表示区分には、その最後に合計の列を置く。
 * 実行消費の消費項目とその表示区分の合計は、USDとクレジットを組にして、USD、クレジットの順に並べる
 */
export function breakdownColumns(
  categories: CategoryColumns[],
  executionCategories: CategoryColumns[],
): BreakdownColumn[] {
  const executionItems = new Set(executionCategories.flatMap((c) => c.items));
  const executionTotals = new Set(
    executionCategories.filter((c) => c.items.length >= 2).map((c) => c.category),
  );
  return categories.flatMap((c) => {
    const groupLabel = categoryLabels[c.category] ?? c.category;
    const single = c.items.length === 1 && (itemLabels[c.items[0]] ?? c.items[0]) === groupLabel;
    const group = single ? null : groupLabel;
    const columns: BreakdownColumn[] = c.items.flatMap((item): BreakdownColumn[] => {
      const label = itemLabels[item] ?? item;
      const credit = (v: BreakdownValues) => v.items[item] ?? null;
      if (!executionItems.has(item)) {
        return [{ key: `item:${item}`, group, label, sub: null, unit: CREDIT_UNIT, value: credit }];
      }
      return [
        {
          key: `usd:item:${item}`,
          group,
          label,
          sub: USD_LABEL,
          value: (v) => v.usd.items[item] ?? null,
        },
        { key: `item:${item}`, group, label, sub: CREDIT_LABEL, value: credit },
      ];
    });
    if (c.items.length >= 2) {
      const credit = (v: BreakdownValues) => v.category_totals[c.category] ?? null;
      if (executionTotals.has(c.category)) {
        columns.push(
          {
            key: `usd:category:${c.category}`,
            group: groupLabel,
            label: '合計',
            sub: USD_LABEL,
            value: (v) => v.usd.category_totals[c.category] ?? null,
          },
          {
            key: `category:${c.category}`,
            group: groupLabel,
            label: '合計',
            sub: CREDIT_LABEL,
            value: credit,
          },
        );
      } else {
        columns.push({
          key: `category:${c.category}`,
          group: groupLabel,
          label: '合計',
          sub: null,
          unit: CREDIT_UNIT,
          value: credit,
        });
      }
    }
    return columns;
  });
}

/** 列の見出しの、表示区分から下の段の並び */
export function breakdownPath(c: BreakdownColumn): string[] {
  return [...(c.group ? [c.group] : []), c.label, ...(c.sub ? [c.sub] : [])];
}

/** 上の段の見出しを、続く列の数とともに並べる（1段の見出しは null） */
export function headerGroups(columns: BreakdownColumn[]): { group: string | null; span: number }[] {
  const out: { group: string | null; span: number }[] = [];
  for (const c of columns) {
    const last = out[out.length - 1];
    if (last && c.group !== null && last.group === c.group) last.span += 1;
    else out.push({ group: c.group, span: 1 });
  }
  return out;
}

/** 取得に失敗したときの文言 */
export function fetchFailureMessage(f: Failure): string {
  const reason = f.message.replace(/。$/, '');
  switch (f.code) {
    case 'CREDIT_DB_UNAVAILABLE':
      return 'credit DBに接続できませんでした。少し待ってから、もう一度取得してください。';
    case 'COMMON_DB_UNAVAILABLE':
      return 'CommonDBから取得できませんでした。少し待ってから、もう一度取得してください。';
    case 'COMPANY_NOT_FOUND':
      return 'この企業はCommonDBにありません。';
    case 'NETWORK':
      return `サーバーに接続できませんでした（${reason}）。`;
    default:
      return `取得できませんでした（${reason}）。`;
  }
}
